import { useQuery } from '@tanstack/react-query'
import { learnerApi, MyCourseResponse, Member } from '@/api/b2b'
import { aiqApi, AiqStatusResponse } from '@/api/aiq'

// ============================================================================
// Types
// ============================================================================

export interface LearningStats {
  overallProgressPercent: number
  inProgressCurriculums: number
  completedCurriculums: number
  totalCurriculums: number
  completedModules: number
  totalModules: number
  completedLessons: number
  totalLessons: number
}

export interface QuickAccessInfo {
  classProductId: number
  contentId: number
  courseTitle: string
  moduleTitle: string | null
  lessonTitle: string | null
  thumbnailUrl: string | null
  hasStarted: boolean
  // 모듈 마무리 퀴즈 관련
  pendingModuleEndQuiz?: {
    moduleId: number
    moduleTitle: string
  } | null
  currentLessonModuleId?: number
  isLastLessonInModule?: boolean
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * MyCourseResponse[]에서 학습 통계 계산
 */
function calculateLearningStats(courses: MyCourseResponse[]): LearningStats {
  let completedModules = 0
  let totalModules = 0
  let completedLessons = 0
  let totalLessons = 0
  let completedCurriculums = 0
  let inProgressCurriculums = 0

  courses.forEach(course => {
    // 레슨 카운트
    completedLessons += course.progress.completedLessons
    totalLessons += course.progress.totalLessons

    // 모듈 카운트
    course.modules.forEach(mod => {
      totalModules++
      if (mod.progressPercent === 100) {
        completedModules++
      }
    })

    // 커리큘럼 상태
    if (course.curriculumStatus === 'completed') {
      completedCurriculums++
    } else if (course.curriculumStatus === 'in_progress') {
      inProgressCurriculums++
    }
  })

  // 전체 진행률 계산
  const overallProgressPercent = totalLessons > 0
    ? Math.round((completedLessons / totalLessons) * 100)
    : 0

  return {
    overallProgressPercent,
    inProgressCurriculums,
    completedCurriculums,
    totalCurriculums: courses.length,
    completedModules,
    totalModules,
    completedLessons,
    totalLessons,
  }
}

/**
 * 다음 학습할 과정 정보 추출 (Quick Access)
 */
function getQuickAccessInfo(courses: MyCourseResponse[]): QuickAccessInfo | null {
  // 진행 중인 코스 찾기 (가장 최근 접속한 것 우선)
  const inProgressCourses = courses
    .filter(c => c.curriculumStatus === 'in_progress')
    .sort((a, b) => {
      const aTime = a.lastAccessedAt ? new Date(a.lastAccessedAt).getTime() : 0
      const bTime = b.lastAccessedAt ? new Date(b.lastAccessedAt).getTime() : 0
      return bTime - aTime
    })

  // 완료됐지만 모듈 마무리 퀴즈가 미완료인 코스
  const completedWithPendingQuiz = courses.filter(c =>
    c.curriculumStatus === 'completed' &&
    c.pendingModuleEndQuiz?.isEnabled &&
    !c.pendingModuleEndQuiz?.isCompleted
  )

  // 진행 중인 코스가 없으면 planned 코스 중 순서가 가장 빠른 것
  const plannedCourses = courses
    .filter(c => c.curriculumStatus === 'planned')
    .sort((a, b) => a.sequenceOrder - b.sequenceOrder)

  const targetCourse = inProgressCourses[0] || completedWithPendingQuiz[0] || plannedCourses[0]
  if (!targetCourse) return null

  // 진행 중인 모듈 찾기
  const currentModule = targetCourse.modules.find(m => m.progressPercent < 100)

  // currentLesson 정보 활용 (있으면 레슨 썸네일 사용)
  const currentLesson = targetCourse.currentLesson
  const pendingQuiz = targetCourse.pendingModuleEndQuiz

  // 대기 중인 모듈 마무리 퀴즈가 있으면 우선 표시
  const hasPendingQuiz = pendingQuiz && pendingQuiz.isEnabled && !pendingQuiz.isCompleted

  return {
    classProductId: targetCourse.classProductId,
    contentId: targetCourse.contentId,
    courseTitle: targetCourse.curriculumBareTitle || targetCourse.curriculumTitle || targetCourse.courseBareTitle || targetCourse.courseTitle || '',
    // 모듈 마무리 퀴즈 대기 시 해당 모듈 정보 우선 표시
    moduleTitle: hasPendingQuiz ? pendingQuiz.moduleTitle : (currentLesson?.moduleTitle || currentModule?.title || null),
    lessonTitle: hasPendingQuiz ? null : (currentLesson?.lessonTitle || null),
    // 모듈 마무리 퀴즈 대기 시 썸네일 없음 (단색 배경 표시)
    thumbnailUrl: hasPendingQuiz ? null : (currentLesson?.thumbnailUrl || targetCourse.thumbnailUrl),
    hasStarted: targetCourse.currentLesson?.hasStarted ?? false,
    // 모듈 마무리 퀴즈 관련 정보
    pendingModuleEndQuiz: hasPendingQuiz
      ? { moduleId: pendingQuiz.moduleId, moduleTitle: pendingQuiz.moduleTitle }
      : null,
    currentLessonModuleId: currentLesson?.moduleId,
    isLastLessonInModule: currentLesson?.isLastLessonInModule,
  }
}

// ============================================================================
// Hooks
// ============================================================================

/**
 * 대시보드 프로필 데이터 훅
 */
export function useDashboardProfile() {
  return useQuery({
    queryKey: ['dashboard', 'profile'],
    queryFn: async () => {
      const response = await learnerApi.getMyProfile()
      return response.data.data
    },
    staleTime: 5 * 60 * 1000, // 5분
  })
}

/**
 * 대시보드 학습 현황 데이터 훅
 */
export function useDashboardLearning() {
  const coursesQuery = useQuery({
    queryKey: ['dashboard', 'courses'],
    queryFn: async () => {
      const response = await learnerApi.getMyCourses()
      return response.data.data
    },
    staleTime: 2 * 60 * 1000, // 2분
  })

  const courses = coursesQuery.data || []
  const stats = calculateLearningStats(courses)
  const quickAccess = getQuickAccessInfo(courses)

  return {
    ...coursesQuery,
    stats,
    quickAccess,
    courses,
  }
}

/**
 * 대시보드 AIQ 상태 훅
 */
export function useDashboardAiq(productId?: number, classProductId?: number) {
  return useQuery({
    queryKey: ['dashboard', 'aiq', productId, classProductId],
    queryFn: async () => {
      if (!productId) return null
      const response = await aiqApi.getStatus(productId, classProductId)
      return aiqApi.transformToFrontendStatus(response.data.data, productId, classProductId || 0)
    },
    enabled: !!productId,
    staleTime: 5 * 60 * 1000,
  })
}

/**
 * 대시보드 전체 데이터 통합 훅
 */
export function useDashboard() {
  const profile = useDashboardProfile()
  const learning = useDashboardLearning()

  // 첫 번째 과정의 productId로 AIQ 상태 조회
  const firstProductId = learning.courses[0]?.productId
  const aiq = useDashboardAiq(firstProductId)

  return {
    profile,
    learning,
    aiq,
    isLoading: profile.isLoading || learning.isLoading,
    isError: profile.isError || learning.isError,
  }
}
